/* rainbow-ring.js - graceful stop for the spinning rainbow ring (22 Aug 2026)

   assets/rainbow-ring.css spins the six bands of a STANDALONE ringed card while it is
   hovered (`--sp-rot` 0 -> 360deg, linear infinite, 3.5s a revolution). CSS alone can only
   stop that dead: the moment :hover goes the animation is gone and the bands snap back to
   their resting angle mid-band.

   So on mouseleave this works out how far through the current revolution the ring is and
   starts one Web Animation on the same ::after that carries `--sp-rot` from exactly there
   to 360deg at exactly the same rate. The revolution finishes, and 360deg paints
   pixel-for-pixel like the 0deg resting ring, so releasing it is invisible.

   The phase has to be captured on ENTER, not on leave: the browser drops :hover before it
   dispatches mouseout, so by the time the leave handler runs the CSS animation is already
   gone and there is nothing left to read. On enter we note the animation's startTime and
   iteration length instead, and on leave the phase is (timeline now - startTime) % length.

   No markup, no classes, no layout: the scope is read straight out of the stylesheet's
   spin list. Reduced motion, no-hover pointers and browsers without animatable pseudo
   elements all fall through to the old behaviour (the ring simply stops). */
(function () {
  'use strict';

  var SEL = [
    '.sp-ring--spin',
    '.dtool',
    '.pfcreate__btn',
    '.bmcreate__card',
    '.supply-collection-hero__card',
    '.laminate-collection-hero__card',
    '.magnet-hero-links__card',
    '.create-hero__card',
    '.create-how__step',
    '.prooflab-hero__card',
    '.supplies-hero-links__card'
  ].join(',');

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var fine = window.matchMedia && window.matchMedia('(hover: hover)');
  if (!document.body || !document.body.animate) return;

  var settling = new WeakMap();
  var phase = new WeakMap();     // host -> { start: timeline time the spin began, dur: ms }

  function stopSettle(host) {
    var a = settling.get(host);
    if (a) {
      settling.delete(host);
      try { a.cancel(); } catch (e) {}
    }
  }

  function spinOf(host) {
    if (!host.getAnimations) return null;
    var list;
    try { list = host.getAnimations({ subtree: true }); } catch (e) { return null; }
    for (var i = 0; i < list.length; i++) {
      if (list[i].animationName === 'sp-ring-spin') return list[i];
    }
    return null;
  }

  // on enter: once the CSS animation exists, remember when it started and how long a
  // revolution is, because none of that can be read any more once the pointer has left
  function track(host) {
    if (phase.has(host)) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {     // by the 2nd frame the animation is running
        if (phase.has(host)) return;
        var spin = spinOf(host);
        if (!spin) return;
        var dur = 0;
        try { dur = spin.effect.getComputedTiming().duration; } catch (e) {}
        if (!dur || !isFinite(dur)) return;
        var start = Number(spin.startTime);
        if (!isFinite(start)) {               // still pending: derive it
          var ct = Number(spin.currentTime);
          var now = Number(document.timeline.currentTime);
          if (!isFinite(ct) || !isFinite(now)) return;
          start = now - ct;
        }
        phase.set(host, { start: start, dur: dur });
      });
    });
  }

  function settle(host) {
    if (reduce && reduce.matches) return;
    if (fine && !fine.matches) return;
    if (settling.has(host)) return;

    var p = phase.get(host);
    phase.delete(host);           // the next hover starts a fresh CSS animation
    if (!p) return;

    var dur = p.dur;
    var now = Number(document.timeline.currentTime);
    if (!isFinite(now)) return;

    var t = now - p.start;
    if (!isFinite(t) || t < 0) return;
    t = t % dur;

    var from = (t / dur) * 360;
    var left = dur - t;
    if (left < 16) return;               // all but finished: let it land on its own

    var anim;
    try {
      anim = host.animate(
        [{ '--sp-rot': from + 'deg' }, { '--sp-rot': '360deg' }],
        { duration: left, easing: 'linear', fill: 'forwards', pseudoElement: '::after' }
      );
    } catch (e) { return; }
    if (!anim) return;

    settling.set(host, anim);
    var done = function () {
      // 360deg and the 0deg resting ring are the same picture, so dropping the
      // animation here is invisible and leaves no Web Animation hanging about
      if (settling.get(host) === anim) stopSettle(host);
    };
    if (anim.finished && anim.finished.then) anim.finished.then(done, function () {});
    else anim.onfinish = done;
  }

  function host(node) {
    return node && node.closest ? node.closest(SEL) : null;
  }

  document.addEventListener('mouseout', function (e) {
    var h = host(e.target);
    if (!h) return;
    if (e.relatedTarget && h.contains(e.relatedTarget)) return;   // still inside the card
    settle(h);
  }, true);

  document.addEventListener('mouseover', function (e) {
    var h = host(e.target);
    if (!h) return;
    stopSettle(h);                       // re-entered: hand it back to the CSS animation
    track(h);
  }, true);
})();
